/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { VPCFlowRecord, FlowStream, DiscoveryIndex } from './types';

// =============================================================================
// VPC FLOW LOG PARSER
// =============================================================================

/**
 * Parse VPC Flow Log text into structured records.
 * 
 * Expected format (space-delimited with timestamp prefix):
 * {timestamp} {eni-id}-all {version} {account-id} {interface-id} {srcaddr} {dstaddr} 
 * {srcport} {dstport} {protocol} {packets} {bytes} {start} {end} {action} {log-status}
 * 
 * Example:
 * 2026-01-09T22:00:16+00:00 eni-03093ab67ec2f8bd0-all 2 561005594433 eni-03093ab67ec2f8bd0 
 * 44.242.28.51 10.237.97.18 443 33930 6 11 1235 1767996016 1767996047 ACCEPT OK
 */
export function parseVPCFlowLogs(text: string): VPCFlowRecord[] {
  const lines = text.trim().split('\n');
  const records: VPCFlowRecord[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Skip empty lines and header lines
    if (!trimmedLine || trimmedLine.startsWith('version') || trimmedLine.startsWith('#')) {
      continue;
    }

    const parts = trimmedLine.split(/\s+/);

    // Need at least 16 fields for a valid record
    if (parts.length < 16) {
      continue;
    }

    // Skip NODATA/SKIPDATA entries
    if (parts[14] === '-' || parts[15] === 'NODATA' || parts[15] === 'SKIPDATA') {
      continue;
    }

    try {
      const record: VPCFlowRecord = {
        timestamp: parts[0],
        eniId: parts[4], // Use interface-id field (more reliable)
        version: parseInt(parts[2], 10),
        accountId: parts[3],
        srcAddr: parts[5],
        dstAddr: parts[6],
        srcPort: parseInt(parts[7], 10) || 0,
        dstPort: parseInt(parts[8], 10) || 0,
        protocol: parseInt(parts[9], 10) || 0,
        packets: parseInt(parts[10], 10) || 0,
        bytes: parseInt(parts[11], 10) || 0,
        startTime: parseInt(parts[12], 10),
        endTime: parseInt(parts[13], 10),
        action: parts[14] as 'ACCEPT' | 'REJECT',
        logStatus: parts[15] as 'OK' | 'NODATA' | 'SKIPDATA',
      };

      // Validate critical fields
      if (record.srcAddr && record.dstAddr && record.action) {
        records.push(record);
      }
    } catch (e) {
      console.warn('Failed to parse flow log line:', line, e);
    }
  }

  return records;
}

// =============================================================================
// PROTOCOL HELPERS
// =============================================================================

/**
 * Get human-readable protocol name from IANA protocol number
 */
export function getProtocolName(protocol: number): string {
  switch (protocol) {
    case 1: return 'ICMP';
    case 6: return 'TCP';
    case 17: return 'UDP';
    case -1: return 'All';
    case 0: return 'HOPOPT';
    case 41: return 'IPv6';
    case 47: return 'GRE';
    case 50: return 'ESP';
    case 51: return 'AH';
    case 58: return 'ICMPv6';
    default: return `Proto-${protocol}`;
  }
}

/**
 * Get protocol number from name
 */
export function getProtocolNumber(name: string): number {
  const normalized = name.toLowerCase();
  switch (normalized) {
    case 'tcp': return 6;
    case 'udp': return 17;
    case 'icmp': return 1;
    case 'all': case '-1': return -1;
    default: return parseInt(name, 10) || 0;
  }
}

// =============================================================================
// IP/CIDR UTILITIES
// =============================================================================

/**
 * Convert IP address string to 32-bit integer
 */
export function ipToInt(ip: string): number {
  const parts = ip.split('.');
  if (parts.length !== 4) return 0;
  return parts.reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
}

/**
 * Check if an IP address is within a CIDR range
 */
export function isIPInCIDR(ip: string, cidr: string): boolean {
  if (!cidr || !ip) return false;
  
  const [cidrIp, prefixLengthStr] = cidr.split('/');
  const prefixLength = parseInt(prefixLengthStr, 10);
  
  if (isNaN(prefixLength) || prefixLength < 0 || prefixLength > 32) {
    return false;
  }

  const ipInt = ipToInt(ip);
  const cidrInt = ipToInt(cidrIp);
  const mask = prefixLength === 0 ? 0 : ((0xFFFFFFFF << (32 - prefixLength)) >>> 0);

  return (ipInt & mask) === (cidrInt & mask);
}

/**
 * Check if IP belongs to any VPC CIDR in the index
 */
export function isIPInVPCCIDR(ip: string, index: DiscoveryIndex): boolean {
  for (const cidr of index.vpcCidrs) {
    if (isIPInCIDR(ip, cidr)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if an IP is private (RFC 1918)
 */
export function isPrivateIP(ip: string): boolean {
  return (
    isIPInCIDR(ip, '10.0.0.0/8') ||
    isIPInCIDR(ip, '172.16.0.0/12') ||
    isIPInCIDR(ip, '192.168.0.0/16') ||
    isIPInCIDR(ip, '100.64.0.0/10') // CGNAT
  );
}

// =============================================================================
// STREAM AGGREGATION
// =============================================================================

/**
 * Aggregate individual flow records into logical streams.
 * A stream is a unique combination of: ENI, src IP, dst IP, src port, dst port, protocol
 */
export function aggregateStreams(
  records: VPCFlowRecord[],
  index: DiscoveryIndex
): FlowStream[] {
  // Filter out invalid records
  const validRecords = records.filter(r => r.logStatus === 'OK');

  const streamMap = new Map<string, FlowStream>();

  for (const record of validRecords) {
    // Create unique stream key
    const key = `${record.eniId}:${record.srcAddr}:${record.dstAddr}:${record.srcPort}:${record.dstPort}:${record.protocol}`;

    // Determine direction by checking if srcAddr belongs to VPC CIDR
    const srcIsInternal = isIPInVPCCIDR(record.srcAddr, index);
    const direction: 'inbound' | 'outbound' = srcIsInternal ? 'outbound' : 'inbound';

    if (!streamMap.has(key)) {
      streamMap.set(key, {
        id: key,
        eniId: record.eniId,
        srcAddr: record.srcAddr,
        dstAddr: record.dstAddr,
        srcPort: record.srcPort,
        dstPort: record.dstPort,
        protocol: record.protocol,
        protocolName: getProtocolName(record.protocol),
        direction,
        action: record.action,
        totalPackets: 0,
        totalBytes: 0,
        flowCount: 0,
        firstSeen: record.timestamp,
        lastSeen: record.timestamp,
        records: [],
      });
    }

    const stream = streamMap.get(key)!;
    stream.totalPackets += record.packets;
    stream.totalBytes += record.bytes;
    stream.flowCount++;
    stream.lastSeen = record.timestamp;
    if (stream.records) {
      stream.records.push(record);
    }

    // Update action if we see a REJECT (REJECT takes precedence)
    if (record.action === 'REJECT') {
      stream.action = 'REJECT';
    }
  }

  // Convert to array and sort by flow count (most active first)
  return Array.from(streamMap.values()).sort((a, b) => b.flowCount - a.flowCount);
}

// =============================================================================
// FORMATTING UTILITIES
// =============================================================================

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format port display (handle ephemeral ports)
 */
export function formatPort(port: number): string {
  if (port === 0) return '*';
  if (port >= 32768 && port <= 65535) return `${port} (ephemeral)`;
  return String(port);
}

